import booleanContains from '@turf/boolean-contains';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { Feature, lineString, Point, point } from '@turf/helpers';
import { useMemo } from 'react';

import {
  convertFormCoordinatesToGeometryCoordinates,
  FormStartAndEndCoordinates,
} from 'modules/form';
import { Coordinate, RouteWaypointCoordinates } from 'modules/route/types';
import { ExclusionZone } from '../types';

type Waypoint = RouteWaypointCoordinates | Feature<Point>;

const isGate = (waypoint: Waypoint): waypoint is Coordinate[] =>
  Array.isArray(waypoint) && Array.isArray(waypoint[0]);

const isWaypointInExclusionZones = (
  waypoint: Waypoint,
  zones: ExclusionZone['exclusionZone'][],
): boolean => {
  if (isGate(waypoint)) {
    const gateLine = lineString(waypoint);
    return zones.some((zone) => booleanContains(zone, gateLine));
  }

  return zones.some((zone) => booleanPointInPolygon(waypoint, zone));
};

export const useCheckCoordinatesInclusion = (
  zones: ExclusionZone[],
): {
  validateStartAndEndCoordinatesWithExclusionZones: (
    exclusionZones: boolean,
    coordinates: FormStartAndEndCoordinates,
  ) => undefined | string;
} => {
  const relevantZones = useMemo(() => {
    return zones
      .filter(({ enabled }) => enabled)
      .map(({ exclusionZone }) => exclusionZone);
  }, [zones]);

  const validateStartAndEndCoordinatesWithExclusionZones = (
    exclusionZones: boolean,
    coordinates: FormStartAndEndCoordinates,
  ): undefined | string => {
    const pointCoordinates =
      convertFormCoordinatesToGeometryCoordinates(coordinates);

    if (!coordinates.route) {
      const points = [point(pointCoordinates[0]), point(pointCoordinates[1])];
      return !!exclusionZones &&
        (relevantZones.some((zone) => booleanPointInPolygon(points[0], zone)) ||
          relevantZones.some((zone) => booleanPointInPolygon(points[1], zone)))
        ? 'Routing start or end point are inside of an exclusion zone.\r\nPlease uncheck this checkbox to continue.'
        : undefined;
    }
    return !!exclusionZones &&
      coordinates.route.some((waypoint) =>
        isWaypointInExclusionZones(waypoint, relevantZones),
      )
      ? 'One or more routing waypoints are inside of an exclusion zone.\r\nPlease uncheck this checkbox to continue.'
      : undefined;
  };

  return { validateStartAndEndCoordinatesWithExclusionZones };
};
